import { useEffect, useRef, useState } from 'react';
import {
  X,
  Maximize2,
  Minimize2,
  // ExternalLink,
  Eye,
  Code2,
  Copy,
  Check,
  Download,
  ChevronDown,
} from 'lucide-react';
import { useUIStore } from '@/stores/uiStore';
import { useConversationsStore } from '@/stores/conversationsStore';
import { LiveProvider, LivePreview, LiveError } from 'react-live';
import * as Recharts from 'recharts';
import React from 'react';
import * as LucideIcons from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import * as d3 from 'd3';
import MermaidDiagram from './MessageBubble/MermaidDiagram';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { loadAILibraries, getAILibraries } from '@/lib/aiLibraries';
import { showToast } from '@/components/Toast';
import { toPng, toSvg } from 'html-to-image';

/**
 * ArtifactViewer 组件
 * 在右侧分屏显示 HTML、JSX 等内容
 * 使用 iframe 隔离样式，避免影响全局
 */
interface ArtifactViewerProps {
  // When mode is 'split', the viewer is rendered inline (50% width) next to ChatPage
  // When mode is 'overlay' (default), it behaves like a right-side floating panel
  mode?: 'split' | 'overlay';
}

export default function ArtifactViewer({
  mode = 'overlay',
}: ArtifactViewerProps) {
  const { artifactViewer, setArtifactViewer, currentView, selectedNavItem } =
    useUIStore();
  const { currentConversationId } = useConversationsStore();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState<'preview' | 'code'>('preview');
  const [copied, setCopied] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [width, setWidth] = useState(50); // 宽度百分比，默认50%
  const [isResizing, setIsResizing] = useState(false);
  const resizeDragRef = useRef<{ startX: number; startWidth: number } | null>(
    null
  );
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [aiLibs, setAiLibs] = useState<any>(getAILibraries());
  const [canDownloadSVG, setCanDownloadSVG] = useState(false);

  const findSvgElement = (): SVGElement | null => {
    if (!artifactViewer) return null;

    if (artifactViewer.type === 'html' && iframeRef.current) {
      const iframeDoc = iframeRef.current.contentDocument;
      const mainChart = iframeDoc?.querySelector(
        'svg[role="application"]'
      ) as SVGElement | null;
      return (
        mainChart ||
        (iframeDoc?.querySelector(
          '.recharts-wrapper svg'
        ) as SVGElement | null) ||
        (iframeDoc?.querySelector('svg') as SVGElement | null) ||
        null
      );
    } else if (
      artifactViewer.type === 'jsx' ||
      artifactViewer.type === 'react'
    ) {
      const mainChart = previewRef.current?.querySelector(
        'svg[role="application"]'
      ) as SVGElement | null;
      return (
        mainChart ||
        (previewRef.current?.querySelector(
          '.recharts-wrapper svg'
        ) as SVGElement | null) ||
        (previewRef.current?.querySelector('svg') as SVGElement | null) ||
        null
      );
    }
    return null;
  };

  // 懒加载 AI 库
  useEffect(() => {
    if (artifactViewer && !aiLibs) {
      loadAILibraries().then(libs => {
        setAiLibs(libs);
      });
    }
  }, [artifactViewer, aiLibs]);

  // Narrow screen detection (mobile)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // We treat "mobile" as coarse pointer devices where either:
    // - width is tablet-or-smaller, OR
    // - height is small (phone landscape) even if width is larger.
    const mqlWidth = window.matchMedia(
      '(pointer: coarse) and (max-width: 1024px)'
    );
    const mqlHeight = window.matchMedia(
      '(pointer: coarse) and (max-height: 520px)'
    );

    const sync = () => setIsNarrowScreen(mqlWidth.matches || mqlHeight.matches);
    sync();

    const onChange = () => sync();
    if (mqlWidth.addEventListener && mqlHeight.addEventListener) {
      mqlWidth.addEventListener('change', onChange);
      mqlHeight.addEventListener('change', onChange);
      return () => {
        mqlWidth.removeEventListener('change', onChange);
        mqlHeight.removeEventListener('change', onChange);
      };
    }

    // Safari < 14 fallback
    (
      mqlWidth as unknown as { addListener: (listener: () => void) => void }
    ).addListener(onChange);
    (
      mqlHeight as unknown as { addListener: (listener: () => void) => void }
    ).addListener(onChange);
    return () => {
      (
        mqlWidth as unknown as {
          removeListener: (listener: () => void) => void;
        }
      ).removeListener(onChange);
      (
        mqlHeight as unknown as {
          removeListener: (listener: () => void) => void;
        }
      ).removeListener(onChange);
    };
  }, []);

  // On narrow screens, make overlay mode full-width to keep it usable
  useEffect(() => {
    if (isFullscreen) return;
    if (mode === 'split') return;

    if (isNarrowScreen) {
      setWidth(100);
    } else {
      // If it was forced to 100% by narrow mode, restore default
      setWidth(prev => (prev === 100 ? 50 : prev));
    }
  }, [isFullscreen, isNarrowScreen, mode]);

  // 为 React Live 预览注入 Tailwind CDN
  useEffect(() => {
    if (
      artifactViewer &&
      (artifactViewer.type === 'jsx' || artifactViewer.type === 'react')
    ) {
      // 检查是否已加载
      if (document.getElementById('tailwind-cdn-for-preview')) return;

      const script = document.createElement('script');
      script.id = 'tailwind-cdn-for-preview';
      script.src = 'https://cdn.tailwindcss.com';
      document.head.appendChild(script);

      return () => {
        // 清理 CDN 脚本
        const el = document.getElementById('tailwind-cdn-for-preview');
        if (el) el.remove();
      };
    }
  }, [artifactViewer]);

  const handleCopy = async () => {
    if (!artifactViewer) return;
    try {
      await navigator.clipboard.writeText(artifactViewer.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  // 下载为 SVG
  const handleDownloadSVG = async () => {
    if (!artifactViewer) return;

    try {
      let elementToCapture: HTMLElement | null = null;

      if (artifactViewer.type === 'html' && iframeRef.current) {
        elementToCapture = iframeRef.current.contentDocument
          ?.body as HTMLElement;
      } else {
        // 对于 JSX, React, Mermaid, Text, Markdown 等，截取外层滚动容器
        // 这样可以确保捕获完整内容，不受滚动位置影响
        elementToCapture = contentRef.current;
      }

      if (!elementToCapture) {
        showToast('No content available to download', 'error', {
          title: 'Download Failed',
        });
        return;
      }

      // 使用 html-to-image 生成 SVG
      const dataUrl = await toSvg(elementToCapture, {
        backgroundColor: 'white',
        width: elementToCapture.scrollWidth,
        height: elementToCapture.scrollHeight,
        style: {
          transform: 'scale(1)',
          overflow: 'visible', // 确保克隆节点不裁剪
          height: 'auto', // 确保高度自适应内容
          maxHeight: 'none', // 移除最大高度限制
        },
      });

      const link = document.createElement('a');
      const fileName =
        artifactViewer.title.replace(/[\\/:*?"<>|]/g, '_').trim() || 'export';
      link.download = `${fileName}.svg`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowDownloadMenu(false);
    } catch (error) {
      console.error('Failed to download SVG:', error);
      showToast('Failed to generate SVG image', 'error', {
        title: 'Download Failed',
      });
    }
  };

  // 下载为 PNG
  const handleDownloadPNG = async () => {
    if (!artifactViewer) return;

    try {
      let elementToCapture: HTMLElement | null = null;

      if (artifactViewer.type === 'html' && iframeRef.current) {
        // 尝试获取 iframe 内部的 body
        elementToCapture = iframeRef.current.contentDocument
          ?.body as HTMLElement;
      } else {
        // 对于 JSX, React, Mermaid, Text, Markdown 等，截取外层滚动容器
        elementToCapture = contentRef.current;
      }

      if (!elementToCapture) {
        showToast('No content available to download', 'error', {
          title: 'Download Failed',
        });
        return;
      }

      // 使用 html-to-image 生成 PNG
      const dataUrl = await toPng(elementToCapture, {
        backgroundColor: 'white', // 确保背景是白色的
        cacheBust: true, // 防止缓存问题
        width: elementToCapture.scrollWidth,
        height: elementToCapture.scrollHeight,
        style: {
          transform: 'scale(1)', // 避免缩放问题
          overflow: 'visible', // 确保克隆节点不裁剪
          height: 'auto', // 确保高度自适应内容
          maxHeight: 'none', // 移除最大高度限制
        },
      });

      const link = document.createElement('a');
      // 保留标题中的中文、字母、数字、连字符、下划线和空格，只替换特殊字符
      const fileName =
        artifactViewer.title.replace(/[\\/:*?"<>|]/g, '_').trim() || 'export';
      link.download = `${fileName}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setShowDownloadMenu(false);
    } catch (error) {
      console.error('Failed to download PNG:', error);
      showToast('Failed to generate PNG image', 'error', {
        title: 'Download Failed',
      });
    }
  };

  // 提取并处理 React 组件代码
  const extractComponent = (
    codeString: string
  ): { code: string; needsInline: boolean } => {
    // 处理 JSX 文本中的 HTML 实体
    // JSX 解析器无法直接处理 &lt; &gt; 等实体
    // 需要将它们转换为 JSX 表达式形式
    let cleaned = codeString
      .replace(/&lt;/g, "{'<'}")
      .replace(/&gt;/g, "{'>'}")
      .replace(/&quot;/g, '{\'"\'}"')
      .replace(/&#39;/g, '{"\'"}')
      .replace(/&amp;/g, "{'&'}");

    // 移除 import 语句
    cleaned = cleaned
      .replace(/import\s+[\s\S]*?from\s+['"].*?['"];?\s*/g, '')
      .trim();

    // 移除 export default（但保留组件定义）
    cleaned = cleaned
      .replace(/export\s+default\s+/g, '')
      .replace(/export\s*\{[^}]*\}\s*;?/g, '')
      .trim();

    // 移除可能的重复分号
    if (cleaned.endsWith(';;')) {
      cleaned = cleaned.slice(0, -1);
    }

    // 寻找顶层 React 组件（只匹配行首的组件定义，排除内部组件和常量）
    // 匹配 function Component() 或 const Component = () => 或 const Component = function()
    // 排除 const CONSTANT = [...] 这种常量定义
    const topLevelComponentRegex =
      /^(?:export\s+default\s+)?(?:function\s+([A-Z]\w+)|(?:const|let|var)\s+([A-Z]\w+)\s*=\s*(?:function|\(|React\.FC))/m;
    const match = cleaned.match(topLevelComponentRegex);

    if (match) {
      const mainComponent = match[1] || match[2];

      if (!cleaned.endsWith(';') && !cleaned.endsWith('}')) {
        cleaned += ';';
      }
      const finalCode = `${cleaned}\n\nrender(<${mainComponent} />);`;
      // 保留所有组件定义（包括内部的），只渲染顶层主组件
      return {
        code: finalCode,
        needsInline: true,
      };
    }

    // 如果没有找到组件，尝试提取函数声明
    const isFunctionDeclaration =
      /^(const|let|var|function)\s+\w+\s*[=(]/.test(cleaned) ||
      /^(const|let|var)\s+\w+\s*:\s*React\.FC/.test(cleaned);

    if (isFunctionDeclaration) {
      const componentNameMatch = cleaned.match(
        /^(?:const|let|var|function)\s+(\w+)/
      );
      const componentName = componentNameMatch
        ? componentNameMatch[1]
        : 'Component';
      if (!cleaned.endsWith(';') && !cleaned.endsWith('}')) {
        cleaned += ';';
      }
      return {
        code: `${cleaned}\n\nrender(<${componentName} />);`,
        needsInline: true,
      };
    }

    return {
      code: cleaned,
      needsInline: false,
    };
  };

  const handleClose = () => {
    setArtifactViewer(null);
  };

  // 自动关闭逻辑：监听侧边栏 tab/conversation 切换
  const prevStateRef = useRef<{ view: any; nav: any; conv: any } | null>(null);
  useEffect(() => {
    if (artifactViewer) {
      // 首次打开时记录当前 tab/conversation 状态
      if (!prevStateRef.current) {
        prevStateRef.current = {
          view: currentView,
          nav: selectedNavItem,
          conv: currentConversationId,
        };
        return;
      }
      // tab 或 conversation 状态发生变化才关闭
      const prev = prevStateRef.current;
      const tabChanged =
        prev.view !== currentView || prev.nav !== selectedNavItem;
      const convChanged = prev.conv !== currentConversationId;
      // tab 命中关闭条件
      const shouldClose =
        currentView === 'chat' ||
        selectedNavItem === 'chats' ||
        selectedNavItem === 'Agents' ||
        selectedNavItem === 'chats-list' ||
        selectedNavItem === 'agents-list' ||
        convChanged;
      if ((tabChanged && shouldClose) || convChanged) {
        setArtifactViewer(null);
        prevStateRef.current = null;
      }
    } else {
      // ArtifactViewer 关闭时重置记录
      prevStateRef.current = null;
    }
  }, [
    currentView,
    selectedNavItem,
    currentConversationId,
    artifactViewer,
    setArtifactViewer,
  ]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // const openInNewWindow = () => {
  //   if (!artifactViewer) return;

  //   const newWindow = window.open('', '_blank', 'width=1200,height=800');
  //   if (!newWindow) return;

  //   if (artifactViewer.type === 'html') {
  //     newWindow.document.write(artifactViewer.content);
  //     newWindow.document.close();
  //   } else if (
  //     artifactViewer.type === 'jsx' ||
  //     artifactViewer.type === 'react'
  //   ) {
  //     // For JSX, we need to render in a similar way
  //     newWindow.document.write(`
  //       <!DOCTYPE html>
  //       <html>
  //         <head>
  //           <title>${artifactViewer.title}</title>
  //           <meta charset="utf-8">
  //         </head>
  //         <body>
  //           <div id="root"></div>
  //           <script>
  //             window.addEventListener('message', (event) => {
  //               if (event.data.type === 'RENDER_JSX') {
  //                 // Handle JSX rendering in new window
  //                 const root = document.getElementById('root');
  //                 if (root) {
  //                   root.innerHTML = '<p>JSX preview in new window - implementation needed</p>';
  //                 }
  //               }
  //             });
  //           </script>
  //         </body>
  //       </html>
  //     `);
  //     newWindow.document.close();
  //   }
  // };

  // Update iframe key when content changes to force recreation and set content
  useEffect(() => {
    if (!artifactViewer) return;
    if (artifactViewer.type !== 'html') return;

    // Force iframe recreation by updating key
    setIframeKey(prev => prev + 1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactViewer?.content]);

  // 处理拖拽调整宽度
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeDragRef.current) return;
      const { startX, startWidth } = resizeDragRef.current;
      const windowWidth = window.innerWidth;
      // 增量计算：鼠标向左移动 → 面板变宽，向右移动 → 面板变窄
      const deltaX = e.clientX - startX;
      const deltaPercent = (deltaX / windowWidth) * 100;
      const newWidth = startWidth - deltaPercent;
      // 限制宽度在 25% 到 75% 之间
      const clampedWidth = Math.min(Math.max(newWidth, 25), 75);
      setWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      resizeDragRef.current = null;
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleResizeStart = (e: React.MouseEvent) => {
    resizeDragRef.current = { startX: e.clientX, startWidth: width };
    setIsResizing(true);
  };
  if (!artifactViewer) return null;

  // Container class depends on fullscreen and rendering mode
  const containerClass = isFullscreen
    ? 'fixed inset-0 z-50 bg-white'
    : mode === 'split'
      ? `relative z-30 flex-shrink-0 h-full bg-white border-l border-gray-300 flex flex-col ${isResizing ? 'select-none' : ''}`
      : `fixed right-0 top-0 bottom-0 z-40 bg-white border-l border-gray-300 shadow-2xl ${isResizing ? 'select-none' : ''}`;

  const containerStyle = isFullscreen
    ? {}
    : mode === 'split'
      ? { width: `${width}%` }
      : { width: `${width}%` };

  return (
    <div
      ref={containerRef}
      className={containerClass}
      style={{
        ...containerStyle,
        cursor: isResizing ? 'col-resize' : 'default',
      }}
    >
      {/* 可拖拽的分隔条 - 放在容器内部左侧 */}
      {!isFullscreen && !isNarrowScreen && (
        <div
          className="group w-2 absolute top-0 bottom-0 left-0 h-full cursor-col-resize -ml-1 z-50 grid place-items-center"
          onMouseDown={handleResizeStart}
        >
          {/* 细线 */}
          <div
            className={`absolute top-0 bottom-0 right-1 w-[0.5px] transition-all ${
              isResizing
                ? 'bg-blue-500 w-px translate-x-[0.5px]'
                : 'bg-gray-300 group-hover:delay-75 group-hover:bg-blue-400 group-hover:w-px group-hover:translate-x-[0.5px]'
            }`}
          />
          {/* 圆形滑块指示器 */}
          <div
            className={`h-6 w-2 relative rounded-full border bg-white shadow transition duration-200 cursor-col-resize ${
              isResizing
                ? 'border-blue-500 bg-blue-500'
                : 'border-gray-300 group-hover:delay-75 group-hover:border-blue-500 group-hover:bg-blue-500'
            }`}
          />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 lg:mt-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <h3 className="font-semibold text-gray-800 truncate min-w-0">
            {artifactViewer.title}
          </h3>
          {/* Tab 切换按钮 - 仅对 JSX/React 类型显示 */}
          {(artifactViewer.type === 'jsx' ||
            artifactViewer.type === 'react') && (
            <div className="flex gap-1 flex-shrink-0">
              <button
                onClick={() => setActiveTab('preview')}
                className={`flex items-center gap-1.5 text-sm rounded transition-colors ${
                  activeTab === 'preview'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                } ${isNarrowScreen ? 'p-2' : 'px-3 py-1.5'}`}
                title="Preview"
              >
                <Eye size={16} />
              </button>
              <button
                onClick={() => setActiveTab('code')}
                className={`flex items-center gap-1.5 text-sm rounded transition-colors ${
                  activeTab === 'code'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                } ${isNarrowScreen ? 'p-2' : 'px-3 py-1.5'}`}
                title="Code"
              >
                <Code2 size={16} />
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* 复制按钮 */}
          <button
            onClick={handleCopy}
            className={`flex items-center gap-1.5 text-sm rounded transition-colors ${
              copied
                ? 'text-green-700 bg-green-100 hover:bg-green-200'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
            } ${isNarrowScreen ? 'p-2' : 'px-3 py-1.5'}`}
            title={copied ? 'Copied!' : 'Copy code'}
          >
            {copied ? (
              <>
                <Check size={16} />
              </>
            ) : (
              <>
                <Copy size={16} />
              </>
            )}
          </button>

          {/* 下载按钮 - 只在 preview 模式显示 */}
          {activeTab === 'preview' && (
            <div className="relative">
              <button
                onClick={() => {
                  if (!showDownloadMenu) {
                    // SVG 下载仍需检查是否有 SVG 元素
                    setCanDownloadSVG(!!findSvgElement());
                  }
                  setShowDownloadMenu(!showDownloadMenu);
                }}
                className={`flex items-center gap-1.5 text-sm rounded transition-colors text-gray-600 hover:text-gray-900 hover:bg-gray-200 ${
                  isNarrowScreen ? 'p-2' : 'px-3 py-1.5'
                }`}
                title="Download"
              >
                <Download size={16} />
                <ChevronDown size={14} />
              </button>

              {/* 下载菜单 */}
              {showDownloadMenu && (
                <>
                  {/* 点击遮罩关闭菜单 */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowDownloadMenu(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-[180px]">
                    <button
                      onClick={handleDownloadSVG}
                      disabled={!canDownloadSVG}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors flex items-center gap-3 ${
                        canDownloadSVG
                          ? 'text-gray-700 hover:bg-gray-100'
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <Download size={16} className="flex-shrink-0" />
                      <span>Download SVG</span>
                    </button>
                    <button
                      onClick={handleDownloadPNG}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-3"
                    >
                      <Download size={16} className="flex-shrink-0" />
                      <span>Download PNG</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="w-px h-6 bg-gray-300" />
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          {/* <button
            onClick={openInNewWindow}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Open in new window"
          >
            <ExternalLink size={16} />
          </button> */}
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        ref={contentRef}
        className={`h-[calc(100%-60px)] overflow-auto bg-white ${isResizing ? 'pointer-events-none' : ''}`}
      >
        {artifactViewer.type === 'html' ? (
          <iframe
            key={iframeKey}
            ref={iframeRef}
            srcDoc={artifactViewer.content}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms"
            title={artifactViewer.title}
          />
        ) : artifactViewer.type === 'jsx' || artifactViewer.type === 'react' ? (
          (() => {
            const { code: cleanedCode, needsInline } = extractComponent(
              artifactViewer.content
            );

            const customStyle = {
              margin: 0,
              padding: '1rem',
              fontSize: '0.875rem',
              backgroundColor: '#fcfcfc',
            };

            return (
              <div className="w-full min-h-full">
                {activeTab === 'preview' ? (
                  <div ref={previewRef} className="w-full min-h-full p-6">
                    <LiveProvider
                      code={cleanedCode}
                      scope={{
                        ...LucideIcons,
                        React,
                        ...React,
                        ...Recharts,
                        d3,
                        ...aiLibs,
                      }}
                      noInline={needsInline}
                    >
                      <div
                        className="bg-white"
                        style={{ minHeight: '100%', width: '100%' }}
                      >
                        <LivePreview />
                      </div>
                      <LiveError className="text-red-600 p-4 text-sm font-mono bg-red-50 border-t border-red-200 sticky bottom-0" />
                    </LiveProvider>
                  </div>
                ) : (
                  /* 源代码显示 */
                  <SyntaxHighlighter
                    language="jsx"
                    style={oneLight}
                    customStyle={customStyle}
                    wrapLongLines={true}
                    showLineNumbers={true}
                    lineProps={() => ({
                      style: { backgroundColor: 'transparent' },
                    })}
                  >
                    {artifactViewer.content}
                  </SyntaxHighlighter>
                )}
              </div>
            );
          })()
        ) : artifactViewer.type === 'text' ? (
          // 纯文本（如小说），也使用 Markdown 渲染以支持基本格式
          <div className="w-full min-h-full p-8">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <h1 className="text-2xl font-bold mb-6 mt-8 first:mt-0 text-gray-900">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-xl font-bold mb-4 mt-6 text-gray-900">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-lg font-semibold mb-3 mt-5 text-gray-900">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="mb-4 leading-8 text-gray-800 text-base">
                    {children}
                  </p>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-gray-300 pl-4 italic my-4 text-gray-600">
                    {children}
                  </blockquote>
                ),
              }}
            >
              {artifactViewer.content}
            </ReactMarkdown>
          </div>
        ) : artifactViewer.type === 'markdown' ? (
          // Markdown 文档（使用 ReactMarkdown 渲染）
          <div className="w-full min-h-full p-8">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => (
                  <h1 className="text-2xl font-bold mb-6 mt-8 first:mt-0 text-gray-900">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-xl font-bold mb-4 mt-6 text-gray-900">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-lg font-semibold mb-3 mt-5 text-gray-900">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="mb-4 leading-8 text-gray-800 text-base">
                    {children}
                  </p>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-gray-300 pl-4 italic my-4 text-gray-600">
                    {children}
                  </blockquote>
                ),
              }}
            >
              {artifactViewer.content}
            </ReactMarkdown>
          </div>
        ) : artifactViewer.type === 'mermaid' ? (
          // Mermaid 图表
          <div className="w-full min-h-full p-6">
            <MermaidDiagram chart={artifactViewer.content} />
          </div>
        ) : artifactViewer.type === 'svg' ? (
          // SVG 图像
          <div className="w-full h-full p-6 flex justify-center items-center">
            <div
              className="max-w-full max-h-full"
              dangerouslySetInnerHTML={{ __html: artifactViewer.content }}
            />
          </div>
        ) : artifactViewer.type === 'code' ? (
          // 通用代码块
          <SyntaxHighlighter
            language={artifactViewer.language || 'text'}
            style={oneLight}
            customStyle={{
              margin: 0,
              padding: '1rem',
              fontSize: '0.875rem',
              backgroundColor: '#fcfcfc',
            }}
            wrapLongLines={true}
            showLineNumbers={true}
            lineProps={() => ({
              style: { backgroundColor: 'transparent' },
            })}
          >
            {artifactViewer.content}
          </SyntaxHighlighter>
        ) : null}
      </div>
    </div>
  );
}
